'use client';

import {
  useCallback,
  useContext,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image';
import { giftingAsset } from 'components/gifting/assets';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { GlobalUIContext } from 'contexts/GlobalUIContext';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { useOccasionDrum } from 'components/gifting/hooks/useOccasionDrum';
import { GIFTING_HEADER_FALLBACK_PX } from 'components/gifting/shared/headerOffset';
import styles from './GiftingOccasions.module.css';

type Occasion = { image: string; name: string };

// Photos load a couple of steps ahead of the active occasion, not all at once.
const PRELOAD_AHEAD = 2;

export const GiftingOccasions = () => {
  const t = useTranslations('gifting');
  const { store } = useRegionLocale();
  const { headerHeight = GIFTING_HEADER_FALLBACK_PX } =
    useContext(GlobalUIContext);

  // The last occasion follows the store's main holiday; other stores get the generic one.
  const seasonal: Occasion =
    store === ShopifyStore.IN
      ? { image: 'diwali', name: t('landing.occasions.items.diwali') }
      : store === ShopifyStore.AE
      ? { image: 'eid', name: t('landing.occasions.items.eid') }
      : store === ShopifyStore.US ||
        store === ShopifyStore.GB ||
        store === ShopifyStore.EU
      ? { image: 'christmas', name: t('landing.occasions.items.christmas') }
      : { image: 'christmas', name: t('landing.occasions.items.holidays') };

  const occasions: Occasion[] = [
    { image: 'retreats', name: t('landing.occasions.items.retreats') },
    { image: 'welcome-kits', name: t('landing.occasions.items.welcomeKits') },
    {
      image: 'wellness-programs',
      name: t('landing.occasions.items.wellnessPrograms'),
    },
    { image: 'milestones', name: t('landing.occasions.items.milestones') },
    { image: 'birthdays', name: t('landing.occasions.items.birthdays') },
    {
      image: 'anniversaries',
      name: t('landing.occasions.items.anniversaries'),
    },
    { image: 'new-parents', name: t('landing.occasions.items.newParents') },
    { image: 'graduations', name: t('landing.occasions.items.graduations') },
    seasonal,
  ];
  const count = occasions.length;

  const section = useRef<HTMLElement>(null);
  const pin = useRef<HTMLDivElement>(null);
  const listWrap = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);

  const [active, setActive] = useState(0);
  const [loadedUpTo, setLoadedUpTo] = useState(PRELOAD_AHEAD);
  const onActive = useCallback((i: number) => {
    setActive(i);
    setLoadedUpTo((n) => Math.max(n, i + PRELOAD_AHEAD));
  }, []);

  const scrollToIndex = useOccasionDrum(
    { section, pin, listWrap, list, frame },
    count,
    headerHeight,
    onActive,
  );

  const vars = {
    '--navh': `${headerHeight}px`,
    '--ocn': count,
  } as CSSProperties;

  return (
    <section
      ref={section}
      className={styles.root}
      style={vars}
      aria-label={t('landing.occasions.label')}
    >
      <div ref={pin} className={styles.pin}>
        <div className={styles.wrap}>
          <div ref={listWrap} className={styles.listWrap}>
            <div ref={list} className={styles.list}>
              {occasions.map((o, i) => (
                <button
                  key={o.image + i}
                  type="button"
                  className={styles.item}
                  aria-current={i === active ? 'true' : undefined}
                  onClick={() => scrollToIndex.current(i)}
                >
                  {o.name}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.side}>
            <div ref={frame} className={styles.frame}>
              {occasions.map((o, i) => (
                <div
                  key={o.image + i}
                  className={styles.layer}
                  aria-hidden={i !== active}
                >
                  {i <= loadedUpTo && (
                    <CustomImage
                      src={giftingAsset(`occ/${o.image}.jpg`)}
                      alt=""
                      fill
                      sizes="(max-width: 900px) calc(100vw - 32px), 32vw"
                      loading="lazy"
                      className={styles.photo}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
